import { CreditIllustration, VariantCreditOffering } from "@/types/cobeTypes";
import { Product, Variant } from "@/types/types";
import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";
import { getVariantsForGroup } from "@/components/Colours/helpers";

export type PaymentPlansView =
  | { kind: "hidden" }
  | {
      kind: "offers";
      primaryOffers: CreditIllustration[];
      klarnaAmountPence?: string;
    }
  | { kind: "klarnaOnly"; klarnaAmountPence: string }
  | { kind: "klarnaPlaceholder" };

const hidden: PaymentPlansView = { kind: "hidden" };

const toPence = (amount: string | number | undefined) =>
  String(Math.round(Number(amount) * 100));

const isKlarnaOffer = (illustration: CreditIllustration) =>
  illustration.creditProvider.toLowerCase() === "klarna";

const getPrimaryOffers = (creditIllustrations: CreditIllustration[]) =>
  creditIllustrations.filter(({ creditProvider }) => creditProvider === "BNPP");

const isVariantKlarnaEligible = (
  skuId: string,
  creditOffersV2: VariantCreditOffering[],
) =>
  creditOffersV2.some(
    (offer) =>
      offer.skuId === skuId && offer.creditIllustrations.some(isKlarnaOffer),
  );

const getSelectedVariantView = (
  variant: Variant,
  creditOffersV2: VariantCreditOffering[],
): PaymentPlansView => {
  const creditIllustrations =
    creditOffersV2.find((offer) => offer.skuId === variant.aliases.skuId)
      ?.creditIllustrations ?? [];

  const primaryOffers = getPrimaryOffers(creditIllustrations);
  const hasKlarnaOffer = creditIllustrations.some(isKlarnaOffer);

  if (primaryOffers.length === 0) {
    return hasKlarnaOffer
      ? { kind: "klarnaOnly", klarnaAmountPence: toPence(variant.price?.value) }
      : hidden;
  }

  return {
    kind: "offers",
    primaryOffers,
    ...(hasKlarnaOffer && {
      klarnaAmountPence: toPence(variant.price?.value),
    }),
  };
};

const getVariantGroupView = (
  product: Product,
  variant: Variant,
  creditOffersV2: VariantCreditOffering[],
): PaymentPlansView => {
  const activeProduct = ensureIsActive(product);
  if (!activeProduct) return hidden;

  const variantGroup = getColourGroupForVariant(
    product.variantGroups,
    variant.id,
  );

  const allVariants: Variant[] = variantGroup
    ? getVariantsForGroup(variantGroup, product)
    : product.variants;

  const isGroupKlarnaEligible = allVariants.some((groupVariant) =>
    isVariantKlarnaEligible(groupVariant.aliases.skuId, creditOffersV2),
  );
  if (!isGroupKlarnaEligible) return hidden;

  const priceRange =
    variantGroup?.variantPriceRange || activeProduct.variantPriceRange;

  const hasPriceRange =
    priceRange?.display?.min !== undefined &&
    priceRange?.display?.max !== undefined &&
    priceRange.display.min !== priceRange.display.max;

  if (hasPriceRange) return { kind: "klarnaPlaceholder" };

  return {
    kind: "klarnaOnly",
    klarnaAmountPence: toPence(priceRange?.value?.min),
  };
};

export const getPaymentPlansView = ({
  product,
  creditOffersV2,
  variant,
  isSelected,
}: {
  product: Product;
  creditOffersV2: VariantCreditOffering[];
  variant: Variant;
  isSelected: boolean;
}): PaymentPlansView =>
  isSelected
    ? getSelectedVariantView(variant, creditOffersV2)
    : getVariantGroupView(product, variant, creditOffersV2);
